import { Component, Inject, ViewChild, OnInit, ViewContainerRef } from '@angular/core';
import { ComponentCatalogComponent } from 'src/app/admintool/piping/dictionaries/component-catalog/component-catalog.component';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Sort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { BehaviorSubject } from 'rxjs';
import { tap, first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import {
  EsraPipingAPIClient,
  ComponentCatalogSearchViewModel,
  SearchResponse_1OfComponentCatalogSearchViewModel,
  SearchRequest,
  FilterableProperty,
  FilterType
} from 'src/app/shared/models/autogenerated-piping';

export interface ComponentOption {
  id: string;
  code: string;
  group: string;
  type: string;
  description: string;
  isActive?: boolean;
  material?: string;
  astm?: string;
  construction?: string;
  designStandard?: string;
  scH1RAT?: string;
  scH2?: string;
  ends?: string;
  selected?: boolean;
}

export interface ComponentCatalogSelectionData {
  availableComponents?: ComponentOption[];
  selectedComponents?: ComponentOption[];
  title?: string;
  groupFilter?: string; // Filter by specific group if needed
}

@Component({
  selector: 'app-component-catalog-modal',
  templateUrl: './component-catalog-modal.component.html',
  styleUrls: ['./component-catalog-modal.component.scss'],
  standalone: false
})
export class ComponentCatalogModalComponent implements OnInit {
  faSearch = faSearch;
  displayedColumns: string[] = ['select', 'code', 'group', 'type', 'description', 'material'];
  dataSource = new MatTableDataSource<ComponentOption>([]);

  @ViewChild(MatPaginator, { static: true }) matPaginator!: MatPaginator;
  @ViewChild('catalogHost', { read: ViewContainerRef, static: false }) catalogHost!: ViewContainerRef;

  searchQuery = '';
  sortBy: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  pageSize: number = 10;
  pageIndex: number = 0;
  totalRecords: number = 0;
  isSearching$ = new BehaviorSubject<boolean>(false);
  searchResults: ComponentOption[] = [];
  selectedComponents: ComponentOption[] = [];
  modalTitle: string = 'Select Components';
  embeddedComponent: any = null;
  private syncTookOver = false;

  // Filters
  groupFilter = '';
  typeFilter = '';
  materialFilter = '';
  designStandardFilter = '';

  // Filter options
  availableGroups: string[] = [];
  availableTypes: string[] = [];
  availableMaterials: string[] = [];
  availableDesignStandards: string[] = [];

  constructor(
    public dialogRef: MatDialogRef<ComponentCatalogModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ComponentCatalogSelectionData,
    private esraPipingAPIClient: EsraPipingAPIClient
  ) {
    this.selectedComponents = data?.selectedComponents ? [...data.selectedComponents] : [];
    this.modalTitle = data?.title || 'Select Components';
    this.groupFilter = data?.groupFilter || '';
  }

  ngOnInit(): void {
    // Only load components if we're not using the embedded admin catalog
    // The embedded catalog will handle its own API calls
    if (!this.groupFilter) {
      this.loadComponents();
    }
  }

  ngAfterViewInit(): void {

    // Only create the embedded admin component if we have a specific group filter
    // For general component catalog (no group filter), use the standard modal functionality
    if (this.groupFilter) {
      // Use setTimeout to ensure the ViewChild is resolved after the *ngIf renders
      setTimeout(() => {

        if (!this.catalogHost) {
          this.loadComponents();
          return;
        }

        this.createEmbeddedComponent();
      }, 0);
    } else {
      // For general component catalog, load components using standard API
      this.loadComponents();
    }
  }

  private createEmbeddedComponent(): void {

    try {
      // Clear any existing components first
      this.catalogHost.clear();

      const compRef = this.catalogHost.createComponent<any>(ComponentCatalogComponent);
      if (compRef) {

        // Store reference to the embedded component
        this.embeddedComponent = compRef.instance;

        // Set inputs
        compRef.instance.isModal = true;
        compRef.instance.initialGroup = this.groupFilter;


        // Trigger change detection to ensure the component initializes
        compRef.changeDetectorRef.detectChanges();
        this.patchEmbeddedSearchMethod(compRef.instance, compRef.changeDetectorRef);
        this.attemptPreSelectionWithRetries(compRef.instance, compRef.changeDetectorRef, 0);

        // Subscribe to output
        if (compRef.instance.selectionConfirmed && compRef.instance.selectionConfirmed.subscribe) {

          compRef.instance.selectionConfirmed.subscribe((selection: any) => {

            // Convert ComponentViewModel[] to ComponentOption[] if needed
            const convertedSelection = this.convertComponentViewModelsToOptions(selection);

            this.dialogRef.close(convertedSelection);
          });
        }
      } else {
        this.loadComponents();
      }
    } catch (e) {
      // If dynamic creation fails, fall back to standard modal
      this.loadComponents();
    }
  }

  private patchEmbeddedSearchMethod(embeddedInstance: any, componentRefCdr: any): void {
    if (!embeddedInstance || !embeddedInstance.search) {
      return;
    }
    // Store the original search method
    const originalSearch = embeddedInstance.search.bind(embeddedInstance);
    embeddedInstance.search = (isSortSearch: boolean = false) => {
      const result = originalSearch(isSortSearch);
      setTimeout(() => {
        this.applySelectionsToCurrentPage(embeddedInstance, componentRefCdr);
      }, 1000);
      return result;
    };
  }

  private applySelectionsToCurrentPage(embeddedInstance: any, componentRefCdr: any): void {
    if (!this.selectedComponents || this.selectedComponents.length === 0) {
      return;
    }

    if (!embeddedInstance._selectedMap || !embeddedInstance.dataSource?.data) {
      return;
    }
    this.syncTookOver = true;

    let syncedCount = 0;

    this.selectedComponents.forEach(existingComponent => {
      const matchInCurrentPage = embeddedInstance.dataSource.data.find((item: any) =>
        item.componentId === existingComponent.id
      );

      if (matchInCurrentPage) {
        if (!embeddedInstance._selectedMap.has(existingComponent.id)) {
          embeddedInstance._selectedMap.set(existingComponent.id, matchInCurrentPage);
        }
        matchInCurrentPage.__selected = true;
        syncedCount++;
      }
    });

    if (syncedCount > 0) {
      // Force re-render
      if (embeddedInstance.dataSource) {
        const currentData = embeddedInstance.dataSource.data;
        embeddedInstance.dataSource.data = [...currentData];
      }

      if (componentRefCdr) {
        componentRefCdr.detectChanges();
      }

      setTimeout(() => {
        this.selectedComponents.forEach(existingComponent => {
        });
      }, 500);
    }
  }

  private attemptPreSelectionWithRetries(embeddedInstance: any, componentRefCdr: any, attemptNumber: number): void {
    const maxAttempts = 5;
    const delays = [2000, 3000, 4000, 5000, 6000];

    if (this.syncTookOver) {
      return;
    }

    if (attemptNumber >= maxAttempts) {
      return;
    }

    const delay = delays[attemptNumber];

    setTimeout(() => {
      if (this.syncTookOver) {
        return;
      }

      const success = this.preSelectExistingComponents(embeddedInstance, componentRefCdr);

      if (!success && attemptNumber < maxAttempts - 1) {
        this.attemptPreSelectionWithRetries(embeddedInstance, componentRefCdr, attemptNumber + 1);
      } else if (success) {
        this.syncTookOver = true;
      }
    }, delay);
  }

  private preSelectExistingComponents(embeddedInstance: any, componentRefCdr: any): boolean {
    if (embeddedInstance && embeddedInstance.isModal !== true) {
      return false;
    }

    if (!this.selectedComponents || this.selectedComponents.length === 0) {
      return false;
    }

    if (!embeddedInstance) {
      return false;
    }

    if (!embeddedInstance._selectedMap) {
      return false;
    }

    if (!embeddedInstance.dataSource?.data) {
      return false;
    }

    let matchCount = 0;
    let noMatchCount = 0;

    this.selectedComponents.forEach(existingComponent => {

      const matchInCurrentPage = embeddedInstance.dataSource.data.find((item: any) => {
        const matches = item.componentId === existingComponent.id;
        return matches;
      });

      if (matchInCurrentPage) {
        matchCount++;
        const key = existingComponent.id;
        embeddedInstance._selectedMap.set(key, matchInCurrentPage);
        matchInCurrentPage.__selected = true;
      } else {
        noMatchCount++;
      }
    });

    if (componentRefCdr) {
      componentRefCdr.detectChanges();
    } else if (embeddedInstance.cdr) {
      embeddedInstance.cdr.detectChanges();
    }

    if (embeddedInstance.dataSource && matchCount > 0) {
      const currentData = embeddedInstance.dataSource.data;
      embeddedInstance.dataSource.data = [...currentData];
    }

    return matchCount > 0;
  }

  private convertComponentViewModelsToOptions(selection: any[]): ComponentOption[] {
    if (!selection || !Array.isArray(selection)) {
      return [];
    }

    return selection.map((item: any) => ({
      id: item.componentId || item.id || '',
      code: item.componentCode || item.code || '',
      group: item.group || '',
      type: item.type || '',
      description: item.description || '',
      material: item.material || '',
      astm: item.astm || '',
      construction: item.construction || '',
      designStandard: item.designStandard || '',
      scH1RAT: item.scH1RAT || item.schRat || '',
      scH2: item.scH2 || '',
      ends: item.ends || item.end || '',
      selected: false
    }));
  }

  loadComponents(): void {
    const indicator = indicate(this.isSearching$);

    const searchRequest = this.buildSearchRequest();

    this.esraPipingAPIClient.componentCatalogSearch(false, searchRequest)
      .pipe(
        first(),
        indicator,
        tap((response: SearchResponse_1OfComponentCatalogSearchViewModel) => {
          this.processSearchResults(response);
          this.updateFilterOptions(response);
        })
      )
      .subscribe({
        error: (error) => {
          console.error('Error loading components:', error);
        }
      });
  }

  private buildSearchRequest(): SearchRequest {
    const filters: FilterableProperty[] = [];

    // Add filters based on current selections
    if (this.groupFilter) {
      const filter = new FilterableProperty({
        displayName: 'Group',
        propertyName: 'group',
        filterOptions: [this.groupFilter],
        filterType: FilterType._0
      });
      filters.push(filter);
    }

    if (this.typeFilter) {
      const filter = new FilterableProperty({
        displayName: 'Type',
        propertyName: 'type',
        filterOptions: [this.typeFilter],
        filterType: FilterType._0
      });
      filters.push(filter);
    }

    if (this.materialFilter) {
      const filter = new FilterableProperty({
        displayName: 'Material',
        propertyName: 'material',
        filterOptions: [this.materialFilter],
        filterType: FilterType._0
      });
      filters.push(filter);
    }

    if (this.designStandardFilter) {
      const filter = new FilterableProperty({
        displayName: 'Design Standard',
        propertyName: 'designStandard',
        filterOptions: [this.designStandardFilter],
        filterType: FilterType._0
      });
      filters.push(filter);
    }

    const orderBy = this.buildOrderByString();

    const requestData: any = {
      page: this.pageIndex + 1,
      perPage: this.pageSize,
      mainSearch: this.searchQuery,
      filters: filters,
      highlightMatches: false
    };

    if (orderBy !== undefined && orderBy !== null && orderBy !== '') {
      requestData.orderBy = orderBy;
    }

    return new SearchRequest(requestData);
  }

  private processSearchResults(response: SearchResponse_1OfComponentCatalogSearchViewModel): void {
    this.totalRecords = response.totalResults || 0;
    const filteredResults: ComponentCatalogSearchViewModel[] = this.filterInactiveDuplicateResults(response.results || []);

    this.searchResults = filteredResults.map((component: ComponentCatalogSearchViewModel) => ({
      id: component.componentId || '',
      code: component.componentCode || '',
      group: component.group || '',
      type: component.type || '',
      description: component.description || '',
      isActive: component.isActive,
      material: component.material || '',
      astm: component.astm || '',
      construction: component.construction || '',
      designStandard: component.designStandard || '',
      scH1RAT: component.scH1RAT || '',
      scH2: component.scH2 || '',
      ends: component.ends || '',
      selected: this.selectedComponents.some(sc => sc.id === component.componentId)
    }));

    this.dataSource.data = this.searchResults;
  }

  private filterInactiveDuplicateResults(results: ComponentCatalogSearchViewModel[]): ComponentCatalogSearchViewModel[] {
    const activeKeys: Set<string> = new Set<string>();
    results.forEach((component: ComponentCatalogSearchViewModel) => {
      if (component.isActive !== false) {
        activeKeys.add(this.buildDuplicateComponentKey(component.group, component.type, component.componentCode));
      }
    });

    return results.filter((component: ComponentCatalogSearchViewModel) => {
      if (component.isActive !== false) {
        return true;
      }
      const componentKey: string = this.buildDuplicateComponentKey(component.group, component.type, component.componentCode);
      return !activeKeys.has(componentKey);
    });
  }

  private buildDuplicateComponentKey(group: string | undefined, type: string | undefined, code: string | undefined): string {
    const normalizedGroup: string = group?.trim().toLowerCase() ?? '';
    const normalizedType: string = type?.trim().toLowerCase() ?? '';
    const normalizedCode: string = code?.trim().toLowerCase() ?? '';
    return `${normalizedGroup}|${normalizedType}|${normalizedCode}`;
  }

  private updateFilterOptions(response: SearchResponse_1OfComponentCatalogSearchViewModel): void {
    // if (response.searchFilters) {
    //   response.searchFilters.forEach(filter => {
    //     switch (filter.filterPropertyName) {
    //       case 'group':
    //         this.availableGroups = filter.options || [];
    //         break;
    //       case 'type':
    //         this.availableTypes = filter.options || [];
    //         break;
    //       case 'material':
    //         this.availableMaterials = filter.options || [];
    //         break;
    //       case 'designStandard':
    //         this.availableDesignStandards = filter.options || [];
    //         break;
    //     }
    //   });
    //}
  }

  onSearch(): void {
    this.pageIndex = 0;
    this.loadComponents();
  }

  onFilterChange(): void {
    this.pageIndex = 0;
    this.loadComponents();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadComponents();
  }

  onSortChange(sort: Sort): void {
    this.sortBy = sort.active;
    this.sortDirection = sort.direction as 'asc' | 'desc';
    this.loadComponents();
  }

  toggleComponentSelection(component: ComponentOption): void {

    const index = this.selectedComponents.findIndex(sc => sc.id === component.id);

    if (index >= 0) {
      this.selectedComponents.splice(index, 1);
      component.selected = false;
    } else {
      this.selectedComponents.push({ ...component, selected: true });
      component.selected = true;
    }

  }

  selectAll(): void {
    this.searchResults.forEach(component => {
      if (!component.selected) {
        this.selectedComponents.push({ ...component, selected: true });
        component.selected = true;
      }
    });
  }

  deselectAll(): void {
    this.searchResults.forEach(component => {
      const index = this.selectedComponents.findIndex(sc => sc.id === component.id);
      if (index >= 0) {
        this.selectedComponents.splice(index, 1);
        component.selected = false;
      }
    });
  }

  clearAllFilters(): void {
    this.searchQuery = '';
    this.groupFilter = '';
    this.typeFilter = '';
    this.materialFilter = '';
    this.designStandardFilter = '';
    this.onFilterChange();
  }

  onConfirm(): void {
    // original confirm without payload (used by previous logic)
    this.dialogRef.close(this.selectedComponents);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  // Called when the embedded component emits selectionConfirmed
  // Called by the Confirm button when the embedded component hasn't emitted
  onModalConfirm(): void {
    // Only try embedded component if we have a group filter (embedded component should exist)
    if (this.groupFilter && this.embeddedComponent) {
      this.embeddedComponent.confirmModalSelection();
      return;
    }

    // Use wrapper's own selection logic for general catalog modal
    if (this.selectedComponents && this.selectedComponents.length > 0) {
      this.dialogRef.close(this.selectedComponents);
      return;
    }
    this.dialogRef.close(null);
  }

  isComponentSelected(component: ComponentOption): boolean {
    return this.selectedComponents.some(sc => sc.id === component.id);
  }

  onComponentToggle(component: ComponentOption, selected: boolean): void {
    if (selected) {
      // Add component if not already selected
      if (!this.isComponentSelected(component)) {
        this.selectedComponents.push(component);
      }
    } else {
      // Remove component if selected
      this.selectedComponents = this.selectedComponents.filter(sc => sc.id !== component.id);
    }
  }

  get hasFiltersApplied(): boolean {
    return !!(this.searchQuery || this.groupFilter || this.typeFilter ||
              this.materialFilter || this.designStandardFilter);
  }

  get selectedCount(): number {
    return this.selectedComponents.length;
  }

  private buildOrderByString(): string | undefined {
    // Map frontend sort fields to actual API field names
    const fieldMapping: { [key: string]: string } = {
      'code': 'code',  // Use 'code' directly as it exists in Azure Search index
      'componentCode': 'code',  // Map componentCode to code as well
      'group': 'group',
      'type': 'type',
      'description': 'description',
      'material': 'material'
    };

    // If no sort is selected, don't send an orderBy clause (avoid defaulting to 'code')
    if (!this.sortBy) {
      return undefined;
    }

    // If we don't have a mapping for the sort field, fall back to the raw sortBy value
    const apiField = fieldMapping[this.sortBy] || this.sortBy;
    return `${apiField} ${this.sortDirection}`;
  }
}
